/**
 * SchedulerTab — the flow-optimizer Scheduler, pre-scoped to one SMSWiser country.
 * Reuses the Optimizer Control Center wholesale (kill-switch banner, stats cards,
 * per-campaign Apply/interval/mode/status table) with the Country filter locked to
 * this page's country, derived from the SMSWiser slug via countryKeyToIso2.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo, useState } from 'react';

import { OptimizerFiltersCard } from '@/features/optimizer-center/components/OptimizerFiltersCard';
import { OptimizerKillSwitchBanner } from '@/features/optimizer-center/components/OptimizerKillSwitchBanner';
import { OptimizerStatsCards } from '@/features/optimizer-center/components/OptimizerStatsCards';
import { OptimizerTableCard } from '@/features/optimizer-center/components/OptimizerTableCard';
import { useOptimizerConfigs } from '@/features/optimizer-center/hooks/useOptimizerConfigs';
import { useOptimizerSchedulerStatus } from '@/features/optimizer-center/hooks/useOptimizerSchedulerStatus';
import type { OptimizerFilters } from '@/features/optimizer-center/optimizer-center.types';
import { TrackerHeldNotices } from '@/features/optimizer-center/tracker-status';
import { computeStats, filterRows } from '@/features/optimizer-center/utils/row-status';
import { countryKeyToIso2 } from '@/utils/country-flag-emoji';

interface SchedulerTabProps {
  /** SMSWiser country slug (e.g. "australia") — pins the Scheduler's country filter. */
  countryKey: string;
}

/** All filters open except the country, which is locked to this page's country. */
const countryScope = (iso2: string): OptimizerFilters => ({
  provider: 'all',
  mode: 'all',
  status: 'all',
  country: iso2,
  tag: 'all',
  search: '',
});

export const SchedulerTab: FC<SchedulerTabProps> = ({ countryKey }) => {
  const iso2 = countryKeyToIso2(countryKey);
  // Default to CUSTOM (flows actually running the optimizer), like /scheduler.
  const [filters, setFilters] = useState<OptimizerFilters>(() => ({
    ...countryScope(iso2),
    mode: 'CUSTOM',
  }));
  const { rows, isLoading, failedProviders, loadingProviders } =
    useOptimizerConfigs();
  const { globalKillSwitch } = useOptimizerSchedulerStatus();

  // Country-only scope (all modes) powers the stats cards + filter options.
  const countryRows = useMemo(
    () => filterRows(rows, countryScope(iso2)),
    [rows, iso2],
  );
  const stats = useMemo(() => computeStats(countryRows), [countryRows]);
  // The table honours the user's filters — country stays pinned (select hidden).
  const filteredRows = useMemo(() => filterRows(rows, filters), [rows, filters]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
      {globalKillSwitch && <OptimizerKillSwitchBanner />}
      <TrackerHeldNotices />
      <OptimizerStatsCards stats={stats} />
      <OptimizerFiltersCard
        rows={countryRows}
        filters={filters}
        onChange={setFilters}
        hideCountry
      />
      <OptimizerTableCard
        rows={filteredRows}
        isLoading={isLoading}
        failedProviders={failedProviders}
        loadingProviders={loadingProviders}
      />
    </div>
  );
};

export default SchedulerTab;
